import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import {
  Gem, MapPin, Phone, Globe, MessageCircle, Instagram, Cigarette, Clock,
  Heart, X, AlertCircle, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { sendBusinessContact } from "../lib/sendRideRequest";

const APP_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Public Custom-Location page — viewed via QR scan or shared link.
 * Path: /places/custom/:id?t={shareToken}
 *
 * - No auth required to view.
 * - Notes / owner_id / share_token never reach this surface (backend scrubs).
 * - Top: dismissible "Open in Habano App" gold banner.
 * - Saved by recipient bumps share_count + notifies creator anonymously.
 */
export default function CustomLocationPublic() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [emblaRef] = useEmblaCarousel({ loop: false, dragFree: true });

  useEffect(() => {
    if (!token) {
      setErr("Share token required");
      return;
    }
    api.get(`/custom-locations/shared/${id}?t=${encodeURIComponent(token)}`)
      .then(({ data }) => setDoc(data))
      .catch((e) => setErr(e.response?.data?.detail || "Location not found"));
    getMe().then(setUser).catch(() => setUser(null));
  }, [id, token]);

  const onSave = async () => {
    if (!user) {
      setShowGuestPrompt(true);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/custom-locations/${id}/save?t=${encodeURIComponent(token)}`);
      setSaved(true);
      toast.success("Saved to My Places");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (err) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-8 text-center" style={{ background: "#0B0B0B" }}>
        <AlertCircle className="w-9 h-9 text-gold mb-4" strokeWidth={1.5} />
        <h1 className="font-display text-2xl text-foreground mb-2">Location unavailable</h1>
        <p className="text-foreground/60 text-sm">{err}</p>
        <Link to="/" className="mt-6 px-6 py-3 text-gold border border-gold-strong">
          Open Habano
        </Link>
      </main>
    );
  }
  if (!doc) {
    return <main className="min-h-screen" style={{ background: "#0B0B0B" }} />;
  }

  const publicUrl = `${APP_URL}/places/custom/${id}?t=${token}`;
  const heroPhoto = (doc.photos && doc.photos[0]?.url) || null;
  const photos = (doc.photos || []).filter((p) => p?.url);

  // If location has been approved → publicly converted business; redirect users.
  if (doc.converted_business_id) {
    navigate(`/places/${doc.converted_business_id}`, { replace: true });
    return null;
  }

  return (
    <main
      data-testid="custom-location-public"
      className="min-h-screen pb-24"
      style={{ background: "#0B0B0B" }}
    >
      {/* Open-in-App banner */}
      {showBanner && (
        <div
          data-testid="open-in-app-banner"
          className="px-4 py-3 flex items-center gap-3"
          style={{
            background: "linear-gradient(90deg, #E8B84B 0%, #b9912f 100%)",
            color: "#0B0B0B",
          }}
        >
          <Link to="/" className="flex-1 font-display text-sm">
            Open in Habano App →
          </Link>
          <button
            type="button"
            onClick={() => setShowBanner(false)}
            aria-label="Dismiss"
            className="p-1"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Habano header */}
      <header className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: "rgba(232,184,75,0.18)" }}>
        <Link to="/" className="font-display text-lg text-gold tracking-tight">
          Habano<span className="text-foreground/40">.app</span>
        </Link>
        <span className="text-[10px] uppercase tracking-widest text-foreground/45">
          Shared Location
        </span>
      </header>

      {/* Photo carousel */}
      <div className="w-full overflow-hidden relative" style={{ aspectRatio: "16/10", background: "#171717" }}>
        {photos.length === 0 ? (
          <div className="absolute inset-0 grid place-items-center">
            <Gem className="w-16 h-16 text-gold opacity-60" strokeWidth={1.5} />
          </div>
        ) : (
          <div className="embla h-full" ref={emblaRef}>
            <div className="embla__container flex h-full">
              {photos.map((p, i) => (
                <div key={i} className="embla__slide flex-[0_0_100%] h-full">
                  <img
                    loading="lazy"
                    src={p.url}
                    alt={`${doc.name} photo ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-6 mt-6">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 border text-[11px] uppercase tracking-widest text-gold" style={{ borderColor: "rgba(232,184,75,0.4)" }}>
          <Gem className="w-3 h-3" strokeWidth={1.5} /> Local Gem
        </span>
        <h1 data-testid="cl-name" className="font-display text-3xl text-foreground mt-3 leading-tight">
          {doc.name}
        </h1>
        {doc.address && (
          <div className="flex items-center gap-1.5 text-[14px] text-foreground/65 mt-2">
            <MapPin className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />
            {doc.address}
          </div>
        )}
        {doc.description && (
          <p className="text-[15px] text-foreground/80 mt-5 leading-relaxed">{doc.description}</p>
        )}

        {(doc.hours || doc.smoking_policy) && (
          <div className="mt-5 space-y-2">
            {doc.hours && (
              <div className="flex items-center gap-2 text-[13px] text-foreground/70">
                <Clock className="w-4 h-4 text-gold" strokeWidth={1.5} /> {doc.hours}
              </div>
            )}
            {doc.smoking_policy && (
              <div className="flex items-center gap-2 text-[13px] text-foreground/70">
                <Cigarette className="w-4 h-4 text-gold" strokeWidth={1.5} />
                {smokingLabel(doc.smoking_policy)}
              </div>
            )}
          </div>
        )}

        {/* Action buttons — Save (primary) + contact */}
        <div className="mt-7 space-y-3">
          <button
            type="button"
            data-testid="save-to-my-places"
            onClick={onSave}
            disabled={saving || saved}
            className="w-full font-display text-base"
            style={{
              minHeight: 52,
              background: saved ? "rgba(232,184,75,0.15)" : "#E8B84B",
              color: saved ? "#E8B84B" : "#0B0B0B",
              border: "1px solid rgba(232,184,75,0.6)",
              borderRadius: 10,
            }}
          >
            <span className="inline-flex items-center gap-2 justify-center">
              <Heart className="w-4 h-4" strokeWidth={1.5} fill={saved ? "#E8B84B" : "none"} />
              {saved ? "Saved to My Places" : "Save to My Places"}
            </span>
          </button>

          <div className="grid grid-cols-2 gap-3">
            {doc.whatsapp && (
              <button
                type="button"
                data-testid="cl-whatsapp"
                onClick={() => sendBusinessContact({ name: doc.name, whatsapp: doc.whatsapp }, { businessId: doc.id, source: "custom-location-public" })}
                className="flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48, background: "transparent", width: "100%" }}
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp
              </button>
            )}
            {doc.instagram && (
              <a
                data-testid="cl-instagram"
                href={`https://instagram.com/${doc.instagram}`}
                target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} /> Instagram
              </a>
            )}
            {doc.phone && (
              <a
                data-testid="cl-phone"
                href={`tel:${doc.phone.replace(/\s/g, "")}`}
                className="flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} /> Call
              </a>
            )}
            {doc.website && (
              <a
                data-testid="cl-website"
                href={doc.website.startsWith("http") ? doc.website : `https://${doc.website}`}
                target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}
              >
                <Globe className="w-4 h-4" strokeWidth={1.5} /> Website
              </a>
            )}
          </div>
        </div>
      </div>

      <footer className="px-6 mt-14 pt-6 pb-10 text-[12px] text-foreground/45 flex items-center justify-between border-t" style={{ borderColor: "rgba(232,184,75,0.10)" }}>
        <span>habano.app</span>
        <span className="space-x-4">
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
        </span>
      </footer>

      {/* Customer prompt */}
      {showGuestPrompt && !user && (
        <div
          className="fixed inset-0 z-40 grid place-items-center px-6"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setShowGuestPrompt(false)}
        >
          <div
            className="w-full max-w-sm p-7"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)", borderRadius: 16 }}
          >
            <h2 className="font-display text-2xl text-foreground">Create a free account</h2>
            <p className="text-[14px] text-foreground/65 mt-2">
              Sign up to save this location and access it from any device.
            </p>
            <Link
              to={`/login?next=${encodeURIComponent(`/places/custom/${id}?t=${token}`)}`}
              data-testid="guest-prompt-signup"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 font-display text-base"
              style={{ minHeight: 52, background: "#E8B84B", color: "#0B0B0B", borderRadius: 10 }}
            >
              Create account <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <button
              type="button"
              onClick={() => setShowGuestPrompt(false)}
              className="mt-3 w-full text-foreground/60 text-sm py-2"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

function smokingLabel(p) {
  return ({
    allowed: "Smoking allowed",
    not_allowed: "Smoking not allowed",
    outdoor_only: "Smoking — outdoor only",
  })[p] || p;
}
